import React, { useContext, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  LocationTargetsEditor as BingMapsLocationTargetsEditor,
  isLegacyLocationAndRadiusEqual,
  BingMapsContextProvider,
  getIsRadiusTarget as defaultGetIsRadiusTarget,
  TARGETING_TYPE,
} from '@bingads-webui-react/bing-maps';
import { BulkLocationsSearchProvider, BulkLocationsSearchDrawer } from '@bingads-webui-react/bulk-locations-search';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Toaster,
  useToastController,
  ToastTitle,
  Toast,
  ToastTrigger,
  ToastBody,
} from '@fluentui/react-toast';
import { Button } from '@fluentui/react-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { mergeLocations } from '../utils/location-target-shared';


// reference: private/new-expert-signup/packages/expert-location-target/src/with-bing-maps-context-provider.js
const BING_MAPS_API_KEY = 'AgRuCtepgsuuCYV9J6S6w1j8ay2JJwnu8f5EPgv4kkoJoIuka8yui-NzKrvlESTQ';

const toasterId = 'bulkLocationsSearchToasterID';

export const SpecificLocationTargetsEditor = (props) => {
  const {
    data,
    setData,
    edmCampaign,
    allowPositiveLocationTargeting,
    allowNegativeLocationTargeting,
    showSearchTypeOptions,
    strings,
    scope,
    defaultSelectedSearchType,
    isBrandAwarenessCampaignScenario,
    showBulkLocations,
  } = props;
  const i18n = useContext(I18nContext);
  const mapRef = useRef();
  const mapBulkLocationsRef = useRef();
  const [isDrawerOpen, setIsBulkLocationsSearchDrawerOpen] = useState(false);
  const { dispatchToast, dismissToast } = useToastController(toasterId);
  const serverContext = useContext(ServerContext);
  const {
    includedLocations,
    excludedLocations,
  } = data;

  const activity = useContext(ActivityContext);

  // TODO: useMemo

  // reference: private/multi-channel/packages/mca-data/src/use-campaign-api-call/use-campaign-api-call.js
  const {
    Token,
  } = serverContext;

  const makeLocationsCall = useCallback((search) => {
    const {
      cid,
      aid,
    } = scope.values;

    return edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Locations.get({
      select: ['ParentLocations'],
      search,
      ...(isBrandAwarenessCampaignScenario ? { IsBrandAwarenessCampaignScenario: true } : {}),
      IsBrandAwarenessCampaignScenario: isBrandAwarenessCampaignScenario,
    }).toPromise()
      .then(response => response)
      .catch(error => console.log(error));
  }, [scope, edmCampaign, isBrandAwarenessCampaignScenario]);

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const makeBulkLocationsCall = useCallback((searchTerms, countryCode) => {
    const {
      cid,
      aid,
    } = scope.values;

    activity.signal('BulkSearchLocations called');

    return edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Locations['Default.BulkSearchLocations']
      .$makeCall({
        query: {
          select: ['Location'],
          ...(countryCode ? { filter: { where: `Location/CountryCode eq '${countryCode}'` } } : {}),
          ...(isBrandAwarenessCampaignScenario ? { servingChannel: 'Xandr' } : {}),
        },
        payload: {
          searchTerms,
        },
      }).toPromise()
      .then(response => response)
      .catch(error => console.log(error));
  }, [scope, edmCampaign, isBrandAwarenessCampaignScenario]);

  const renderToaster = useCallback((toastId, intent, toastTitle, toastMessage) =>
    dispatchToast(
      <Toast>
        <ToastTitle action={
          <ToastTrigger>
            <Button
              appearance="subtle"
              aria-label={i18n.getString(_TL_('Close'))}
              icon={<Dismiss24Regular />}
              onClick={() => dismissToast(toastId)}
            />
          </ToastTrigger>
        }
        >
          {toastTitle}
        </ToastTitle>
        {toastMessage && <ToastBody>{toastMessage}</ToastBody>}
      </Toast>,
      {
        toastId, pauseOnHover: true, timeout: 8000, position: 'bottom-start', intent,
      }
    ), [dispatchToast, i18n]);

  const handleBulkLocationsSearchDrawerOpen = useCallback(() => {
    activity.signal('Add Bulk Locations panel opens');
    setIsBulkLocationsSearchDrawerOpen(true);
  }, [setIsBulkLocationsSearchDrawerOpen]);

  const searchOptions = useMemo(() => ({
    getLocations: makeLocationsCall,
    skipBingMapAPISearch: isBrandAwarenessCampaignScenario,
  }), [isBrandAwarenessCampaignScenario, makeLocationsCall]);

  const maxItems = 1000;
  const getIsRadiusTarget = useCallback((location, targetingType) => {
    if (targetingType) {
      return defaultGetIsRadiusTarget(location, targetingType) &&
        targetingType === TARGETING_TYPE.Included;
    }
    return defaultGetIsRadiusTarget(location, targetingType);
  }, []);

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const setBulkLocationsData = useCallback((targetedLocations) => {
    const result = mergeLocations(includedLocations, excludedLocations, targetedLocations);
    activity.signal('Add Locations Button clicked')

    if (result.conflictingCount > 0) {
      renderToaster('bulkLocationConflict', 'warning', i18n.getString(_TL_('Unable to add locations')), i18n.getString(_TL_('{{count}} location(s) cannot be added due to targeting conflicts.'), { count: result.conflictingCount }));
    }
    if (result.duplicateCount > 0) {
      renderToaster('bulkLocationSuccess', 'success', i18n.getString(_TL_('Duplicate locations removed')), i18n.getString(_TL_('We automatically removed {{count}} duplicate location(s).'), { count: result.duplicateCount }));
    }

    setData({
      includedLocations: result.includedLocations,
      excludedLocations: result.excludedLocations,
    });
  }, [includedLocations, excludedLocations, setData, renderToaster, i18n]);

  return (
    <div className="specific-location-targets-editor">
      <Toaster toasterId={toasterId} />
      <BulkLocationsSearchProvider
        mapRef={mapBulkLocationsRef}
        onBulkLocationsSearch={makeBulkLocationsCall}
        onAddLocations={setBulkLocationsData}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsBulkLocationsSearchDrawerOpen}
        campaignApiToken={Token}
        bingMapsApiKey={BING_MAPS_API_KEY}
        campaignApiServiceRoot={`${serverContext.CampaignAPI}${serverContext.CampaignAPIPath}`}
      >
        <BulkLocationsSearchDrawer />
      </BulkLocationsSearchProvider>

      <BingMapsContextProvider
        campaignApiToken={Token}
        bingMapsApiKey={BING_MAPS_API_KEY}
        searchOptions={searchOptions}
      >
        <BingMapsLocationTargetsEditor
          mapRef={mapRef}
          onBulkLocationButtonClick={showBulkLocations ? handleBulkLocationsSearchDrawerOpen : undefined}
          allowPositiveLocationTargeting={allowPositiveLocationTargeting}
          allowNegativeLocationTargeting={allowNegativeLocationTargeting}
          showSearchTypeOptions={showSearchTypeOptions}
          strings={strings}
          defaultSelectedSearchType={defaultSelectedSearchType}
          locationTargets={includedLocations}
          negativeLocationTargets={excludedLocations}
          isRadiusExcludeEnabled={false}
          maxLocationTargets={maxItems}
          maxNegativeLocationTargets={maxItems}
          onLocationTargetsChange={bingMapLocations => setData({ includedLocations: bingMapLocations })}
          onNegativeLocationTargetsChange={bingMapLocations => setData({ excludedLocations: bingMapLocations })}
          customizedLocationEqual={isLegacyLocationAndRadiusEqual}
          getIsRadiusTarget={getIsRadiusTarget}
        />
      </BingMapsContextProvider>
    </div>
  );
};

SpecificLocationTargetsEditor.propTypes = {
  setData: PropTypes.func.isRequired,
  data: PropTypes.shape({}),
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  edmCampaign: PropTypes.shape({}).isRequired,
  allowPositiveLocationTargeting: PropTypes.bool,
  allowNegativeLocationTargeting: PropTypes.bool,
  showSearchTypeOptions: PropTypes.bool,
  showBulkLocations: PropTypes.bool,
  strings: PropTypes.shape({}),
  defaultSelectedSearchType: PropTypes.string,
  isBrandAwarenessCampaignScenario: PropTypes.bool,
};

SpecificLocationTargetsEditor.defaultProps = {
  data: {},
  allowPositiveLocationTargeting: true,
  allowNegativeLocationTargeting: true,
  showSearchTypeOptions: true,
  showBulkLocations: false,
  strings: {},
  defaultSelectedSearchType: undefined,
  isBrandAwarenessCampaignScenario: false,
};
